import React, { useState, useEffect, useRef } from 'react';
import { Tab, Nav } from 'react-bootstrap';
import axiosClient from '../axios-client.js';
import swal from 'sweetalert';
import AdminSchedule from './AdminSchedule';

const AdminView = () => {
  const [activeTab, setActiveTab] = useState('notificaciones');

  // ── Reportería ───────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [reportYear,    setReportYear]    = useState(String(currentYear));
  const [reportMonth,   setReportMonth]   = useState('');
  const [reportSort,    setReportSort]    = useState('total_attended');
  const [reportData,    setReportData]    = useState([]);
  const [reportPag,     setReportPag]     = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const loadReport = (year, month, sort, page = 1) => {
    setLoadingReport(true);
    const params = { year, sort, page };
    if (month) params.month = month;
    axiosClient.get('/admin/reports/users', { params })
      .then(({ data }) => { setReportData(data.data); setReportPag(data.pagination); })
      .finally(() => setLoadingReport(false));
  };

  useEffect(() => {
    if (activeTab === 'reporteria') loadReport(reportYear, reportMonth, reportSort);
  }, [activeTab]);

  // ── Notification form ────────────────────────────────────────
  const [notifType, setNotifType]         = useState('global');
  const [notifTitle, setNotifTitle]       = useState('');
  const [notifMessage, setNotifMessage]   = useState('');
  const [notifIcon, setNotifIcon]         = useState('');
  const [notifActionUrl, setNotifActionUrl] = useState('');
  const [notifExpiresAt, setNotifExpiresAt] = useState('');
  const [sending, setSending]             = useState(false);

  // Targeted: user search
  const [userSearch, setUserSearch]     = useState('');
  const [userResults, setUserResults]   = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (notifType !== 'targeted' || !userSearch) {
      setUserResults([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      axiosClient.get('/users', { params: { search: userSearch } })
        .then(({ data }) => setUserResults(data.data ?? []));
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [userSearch, notifType]);

  const selectUser = (u) => {
    setSelectedUser(u);
    setUserSearch(u.name);
    setUserResults([]);
  };

  const resetForm = () => {
    setNotifTitle('');
    setNotifMessage('');
    setNotifIcon('');
    setNotifActionUrl('');
    setNotifExpiresAt('');
    setUserSearch('');
    setSelectedUser(null);
    setUserResults([]);
  };

  const sendNotification = (e) => {
    e.preventDefault();
    if (notifType === 'targeted' && !selectedUser) {
      swal('Atención', 'Selecciona un usuario para la notificación dirigida.', 'warning');
      return;
    }
    setSending(true);
    const payload = {
      title:      notifTitle,
      message:    notifMessage,
      type:       notifType,
      ...(notifIcon       && { icon:       notifIcon }),
      ...(notifActionUrl  && { action_url: notifActionUrl }),
      ...(notifExpiresAt  && { expires_at: notifExpiresAt }),
      ...(notifType === 'targeted' && { user_id: selectedUser.id }),
    };
    axiosClient.post('/admin/notifications', payload)
      .then(() => {
        swal('¡Enviado!', 'Notificación enviada correctamente.', 'success');
        resetForm();
      })
      .catch(() => swal('Error', 'No se pudo enviar la notificación.', 'error'))
      .finally(() => setSending(false));
  };

  return (
    <div className="container mt-4" style={{ paddingTop: '70px' }}>
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="reporteria">Reportería</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="notificaciones">Notificaciones</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="horarios">Horarios</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* ── Notificaciones ─────────────────────────────────── */}
          <Tab.Pane eventKey="notificaciones">
            <div style={{ maxWidth: 560 }}>
              <p className="semi-bold mb-3">Enviar notificación</p>

              {/* Type toggle */}
              <div className="d-flex gap-3 mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="tipo-global"
                    value="global"
                    checked={notifType === 'global'}
                    onChange={() => { setNotifType('global'); resetForm(); }}
                  />
                  <label className="form-check-label" htmlFor="tipo-global">Global (todos los usuarios)</label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="tipo-targeted"
                    value="targeted"
                    checked={notifType === 'targeted'}
                    onChange={() => { setNotifType('targeted'); resetForm(); }}
                  />
                  <label className="form-check-label" htmlFor="tipo-targeted">Específica (un usuario)</label>
                </div>
              </div>

              <form onSubmit={sendNotification}>
                {/* User search — only for targeted */}
                {notifType === 'targeted' && (
                  <div className="mb-3" style={{ position: 'relative' }}>
                    <label className="form-label">Usuario</label>
                    <input
                      className="form-control"
                      placeholder="Buscar por nombre o correo…"
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setSelectedUser(null); }}
                    />
                    {userResults.length > 0 && (
                      <ul
                        className="list-group"
                        style={{
                          position: 'absolute', zIndex: 10, width: '100%',
                          maxHeight: 200, overflowY: 'auto',
                        }}
                      >
                        {userResults.map((u) => (
                          <li
                            key={u.id}
                            className="list-group-item list-group-item-action"
                            style={{ cursor: 'pointer' }}
                            onClick={() => selectUser(u)}
                          >
                            {u.name} <span className="text-muted" style={{ fontSize: '0.85rem' }}>— {u.email}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Título</label>
                  <input
                    className="form-control"
                    required
                    value={notifTitle}
                    onChange={(e) => setNotifTitle(e.target.value)}
                    placeholder="Ej. Nueva clase disponible"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Mensaje</label>
                  <textarea
                    className="form-control"
                    required
                    rows={4}
                    value={notifMessage}
                    onChange={(e) => setNotifMessage(e.target.value)}
                    placeholder="Escribe el contenido de la notificación…"
                  />
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <label className="form-label">
                      Ícono <span className="text-muted" style={{ fontSize: '0.8rem' }}>(opcional)</span>
                    </label>
                    <select
                      className="form-select"
                      value={notifIcon}
                      onChange={(e) => setNotifIcon(e.target.value)}
                    >
                      <option value="">— Sin ícono —</option>
                      <option value="bell">🔔 Bell</option>
                      <option value="calendar">📅 Calendar</option>
                      <option value="user">👤 User</option>
                      <option value="star">⭐ Star</option>
                      <option value="info">ℹ️ Info</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label">
                      Expiración <span className="text-muted" style={{ fontSize: '0.8rem' }}>(opcional)</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={notifExpiresAt}
                      onChange={(e) => setNotifExpiresAt(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">
                    URL de acción <span className="text-muted" style={{ fontSize: '0.8rem' }}>(opcional — al hacer clic en la notificación se redirige aquí)</span>
                  </label>
                  <input
                    className="form-control"
                    placeholder="Ej. /reservar-clases"
                    value={notifActionUrl}
                    onChange={(e) => setNotifActionUrl(e.target.value)}
                  />
                </div>

                <button className="btn btn-primary" type="submit" disabled={sending}>
                  {sending ? 'Enviando…' : 'Enviar notificación'}
                </button>
              </form>
            </div>
          </Tab.Pane>

          {/* ── Horarios ───────────────────────────────────────── */}
          <Tab.Pane eventKey="horarios">
            <AdminSchedule embedded />
          </Tab.Pane>

          {/* ── Reportería ─────────────────────────────────────── */}
          <Tab.Pane eventKey="reporteria">
            <div className="d-flex align-items-end gap-3 mb-4 flex-wrap">
              <div>
                <label className="form-label mb-1">Año</label>
                <select
                  className="form-select form-select-sm"
                  value={reportYear}
                  onChange={(e) => setReportYear(e.target.value)}
                >
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label mb-1">Mes</label>
                <select
                  className="form-select form-select-sm"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                >
                  <option value="">Todo el año</option>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
                    .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label mb-1">Ordenar por</label>
                <select
                  className="form-select form-select-sm"
                  value={reportSort}
                  onChange={(e) => setReportSort(e.target.value)}
                >
                  <option value="total_attended">Clases asistidas</option>
                  <option value="total_purchases">Paquetes comprados</option>
                </select>
              </div>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => loadReport(reportYear, reportMonth, reportSort)}
                disabled={loadingReport}
              >
                {loadingReport ? 'Cargando…' : 'Consultar'}
              </button>
            </div>

            {loadingReport ? (
              <div className="text-center py-4"><div className="spinner-border" /></div>
            ) : reportData.length === 0 ? (
              <p className="text-muted">Sin datos para el período seleccionado.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Paquetes comprados</th>
                      <th>Clases asistidas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, i) => (
                      <tr key={row.id}>
                        <td>{i + 1}</td>
                        <td>{row.name}</td>
                        <td>{row.email}</td>
                        <td>{row.total_purchases}</td>
                        <td><strong>{row.total_attended}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {reportPag && (reportPag.prev_page_url || reportPag.next_page_url) && (
              <div className="pagination mt-3">
                {reportPag.prev_page_url && (
                  <button onClick={() => loadReport(reportYear, reportMonth, reportSort, reportPag.current_page - 1)}>
                    Anterior
                  </button>
                )}
                <span className="ms-3 me-3">{reportPag.current_page} / {reportPag.last_page}</span>
                {reportPag.next_page_url && (
                  <button onClick={() => loadReport(reportYear, reportMonth, reportSort, reportPag.current_page + 1)}>
                    Siguiente
                  </button>
                )}
              </div>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default AdminView;